import { Stack, Text } from "@mantine/core"
import { QueryKey } from "@tanstack/react-query"
import { Button, Card, EqualWidthGrid, Accordion } from "@tfg/ui"
import _ from "lodash"
import { useTranslation } from "react-i18next"

import {
  CUSTOMISATION_CATEGORIES,
  CustomisationCategoryWithoutNone,
} from "@tfg/types/constants/communication-studio.constant"
import { ScopedConfigurationsResponse } from "@tfg/types/types/communication-studio/configurations.types"
import { StylingParameters } from "@tfg/types/types/communication-studio/styling-parameters.types"

import { queryClient } from "~/backend/queryClient"
import {
  getDesignTokensQueryKey,
  GetProductPageComponentsResponseDto,
  useUpdateDesignTokens,
} from "~/gen/api"
import { useTranslationsMapper } from "~/pages/Brand/CommunicationStudio/DesignStudio/utils/translationsMapper"
import { useProductSheetGroup } from "~/pages/Brand/CommunicationStudio/contexts/productSheetGroup/useProductSheetGroup"
import { showAxiosError } from "~/utils/showAxiosError"
import { showSuccessNotification } from "~/utils/showSuccessNotification"

import { useDesignTokenEditor } from "../contexts/designTokenEditor/useDesignTokenEditor"
import {
  getAvailableCategoriesFromComponents,
  getDesignTokensQueryResult,
  getOrderedCategories,
} from "../utils/utils"

import { DesignTokensEditor } from "./DesignTokensEditor"

export const DesignTokensForm = () => {
  const { categoriesTranslations } = useTranslationsMapper()
  const { t } = useTranslation(["communicationStudio", "common"])
  const {
    styleData,
    setStyleData,
    errors,
    isDirty,
    setIsDirty,
    setConfigurations,
    configurations,
    productPageComponents,
  } = useDesignTokenEditor()

  const { selectedProductSheetGroup } = useProductSheetGroup()
  const productSheetGroupId = selectedProductSheetGroup?.id

  const designTokensQueryKey: QueryKey = productSheetGroupId
    ? getDesignTokensQueryKey(productSheetGroupId)
    : ["designTokens", "undefined"]

  const { mutate: updateDesignTokens } = useUpdateDesignTokens({
    mutation: {
      onSuccess: (_data, _variables) => {
        showSuccessNotification({
          title: t("communicationStudio:design.update.success.title"),
          message: t("communicationStudio:design.update.success.message"),
        })
      },
      onError: (error: unknown) => {
        queryClient.invalidateQueries({
          queryKey: designTokensQueryKey,
        })
        showAxiosError({
          error: error as Error,
          notificationTitle: t("communicationStudio:design.update.error.title"),
          notificationMessage: t(
            "communicationStudio:design.update.error.message"
          ),
          showNotification: true,
        })
      },
    },
  })

  const getPreviousStyleData = (): StylingParameters | undefined => {
    const designTokensQueryResult =
      getDesignTokensQueryResult(designTokensQueryKey)
    return designTokensQueryResult?.stylingParameters as StylingParameters
  }

  const getPreviousConfigurations = ():
    | ScopedConfigurationsResponse
    | undefined => {
    const designTokensQueryResult =
      getDesignTokensQueryResult(designTokensQueryKey)
    return designTokensQueryResult?.configurations
  }

  const handleReset = () => {
    const previousStyleData = getPreviousStyleData()
    if (!previousStyleData) return
    setStyleData(previousStyleData)
    const previousConfigurations = getPreviousConfigurations()
    if (!previousConfigurations) return
    setConfigurations(previousConfigurations)
    setIsDirty(false)
  }

  const handleSave = async () => {
    if (!productSheetGroupId) return
    const previousStyleData = getPreviousStyleData()
    const previousConfigurations = getPreviousConfigurations()
    await updateDesignTokens({
      productSheetGroupId,
      data: {
        stylingParameters: {
          previous: { categories: previousStyleData },
          current: { categories: styleData },
        },
        configurations: {
          previous: previousConfigurations,
          current: configurations,
        },
      },
    })
  }

  const orderedCategories = getOrderedCategories(productPageComponents ?? [])

  return (
    <Card w="100%" h="100%">
      <Stack gap="md" h="-webkit-fill-available" style={{ overflow: "auto" }}>
        {orderedCategories.map((category) =>
          categoryContainsStylingOrConfiguration(
            category,
            productPageComponents ?? []
          ) ? (
            <Accordion key={category} variant="filled">
              <Accordion.Item value={category}>
                <Accordion.Control>
                  <Text>{categoriesTranslations[category]}</Text>
                </Accordion.Control>
                <Accordion.Panel bg="white">
                  <Stack gap="xs" py="sm" px="xs">
                    <DesignTokensEditor category={category} />
                  </Stack>
                </Accordion.Panel>
              </Accordion.Item>
            </Accordion>
          ) : null
        )}
      </Stack>
      <EqualWidthGrid p="sm">
        <Button variant="secondary" fullWidth onClick={handleReset}>
          {t("common:form.reset")}
        </Button>
        <Button
          variant="primary"
          fullWidth
          onClick={handleSave}
          disabled={!_.isEmpty(errors) || !isDirty}
        >
          {t("common:form.save")}
        </Button>
      </EqualWidthGrid>
    </Card>
  )
}

export const categoryContainsStylingOrConfiguration = (
  category: CustomisationCategoryWithoutNone,
  productPageComponents: GetProductPageComponentsResponseDto[]
) => {
  if (category === CUSTOMISATION_CATEGORIES.SEARCH_ENGINE) {
    return true
  }
  const availableCategories = getAvailableCategoriesFromComponents(
    productPageComponents
  )
  return availableCategories.includes(category)
}
